import React, { useMemo, useState } from 'react';
import { useDebounce } from '@ais-ui/custom-hooks';
import {
  AutoCompleteShipInfo,
  useAutocompleteDbShips,
} from '@ais-ui/websockets';
import {
  Badge,
  Button,
  Command,
  Input,
  Label,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  SearchInput,
} from '@ais-ui/ui-core';
import { XIcon } from 'lucide-react';
import { Command as CommandPrimitive } from 'cmdk';
import { useInfiniteSearchShips } from '../Map/Search';
import { cn } from '@ais-ui/utils';
import { PiBoatThin, PiInfo } from 'react-icons/pi';
import { ShipSearchResult } from '../Map/Search';
import { useLocalization } from '@ais-ui/locale-management';
import { getShipDisplayName } from './shipDisplayUtils';

interface ManualVesselEntryProps {
  onAdd: (vessel: AutoCompleteShipInfo) => void;
  onCancel: () => void;
}

const ManualVesselEntry = ({ onAdd, onCancel }: ManualVesselEntryProps) => {
  const { messages } = useLocalization();
  const [name, setName] = useState('');
  const [mmsi, setMmsi] = useState('');
  const [imo, setImo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Validation: at least one of MMSI or IMO is required
    if (!mmsi.trim() && !imo.trim()) {
      setError(
        messages.shipCommandManualEntryValidation ||
          'At least MMSI or IMO is required',
      );
      return;
    }

    // Validate MMSI format (should be numeric, typically 9 digits)
    if (mmsi.trim() && !/^\d+$/.test(mmsi.trim())) {
      setError(
        messages.shipCommandManualEntryMmsiInvalid || 'MMSI must be a number',
      );
      return;
    }

    // Validate IMO format (should be numeric, typically 7 digits)
    if (imo.trim() && !/^\d+$/.test(imo.trim())) {
      setError(
        messages.shipCommandManualEntryImoInvalid || 'IMO must be a number',
      );
      return;
    }

    // Note: The report form schema expects non-nullable strings (empty strings, not null)
    // to match the format from the autocomplete search results
    // Name should be the actual vessel name (or empty), display formatting is handled in the UI
    const vessel: AutoCompleteShipInfo = {
      name: name.trim(),
      mmsi: mmsi.trim() || '',
      imo: imo.trim() || '',
      callSign: '',
      type: '',
    };

    onAdd(vessel);
  };

  return (
    <div className="p-4 space-y-4 w-72">
      <div className="font-medium text-sm">
        {messages.shipCommandManualEntryTitle || 'Add vessel manually'}
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="manual-vessel-name" className="text-xs">
            {messages.shipCommandManualEntryName || 'Name'}
          </Label>
          <Input
            id="manual-vessel-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={
              messages.shipCommandManualEntryNamePlaceholder ||
              'Vessel name (optional)'
            }
            className="h-8"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="manual-vessel-mmsi" className="text-xs">
            {messages.shipCommandManualEntryMmsi || 'MMSI'}
          </Label>
          <Input
            id="manual-vessel-mmsi"
            value={mmsi}
            onChange={(e) => {
              setMmsi(e.target.value);
              setError(null);
            }}
            placeholder={
              messages.shipCommandManualEntryMmsiPlaceholder || 'e.g. 123456789'
            }
            className="h-8"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="manual-vessel-imo" className="text-xs">
            {messages.shipCommandManualEntryImo || 'IMO'}
          </Label>
          <Input
            id="manual-vessel-imo"
            value={imo}
            onChange={(e) => {
              setImo(e.target.value);
              setError(null);
            }}
            placeholder={
              messages.shipCommandManualEntryImoPlaceholder || 'e.g. 1234567'
            }
            className="h-8"
          />
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="flex justify-end space-x-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            {messages.cancel || 'Cancel'}
          </Button>
          <Button type="submit" size="sm">
            {messages.add || 'Add'}
          </Button>
        </div>
      </form>
    </div>
  );
};

interface MultiShipCommandProps {
  value: AutoCompleteShipInfo[];
  onChange: (value: AutoCompleteShipInfo[]) => void;
}

export const MultiShipCommand = ({
  value = [], // Add default empty array
  onChange,
}: MultiShipCommandProps) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const commandRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [showManualEntry, setShowManualEntry] = React.useState(false);
  const { messages } = useLocalization();

  const [searchString, setSearchString] = useState('');
  const bouncedSearchString = useDebounce(searchString, 200);

  const {
    data: searchShips,
    isLoading: isQueryLoading,
    isFetching,
  } = useAutocompleteDbShips(bouncedSearchString);

  // Track which search string we last completed a fetch for (ref to avoid re-renders)
  const lastCompletedFetchRef = React.useRef<string | null>(null);

  // Update ref when fetch completes for current search string
  if (!isQueryLoading && !isFetching && bouncedSearchString.length >= 3) {
    if (lastCompletedFetchRef.current !== bouncedSearchString) {
      lastCompletedFetchRef.current = bouncedSearchString;
    }
  }

  // Derived state - no useState/useEffect needed
  const isLoading =
    isQueryLoading ||
    isFetching ||
    (searchString.length >= 3 && searchString !== bouncedSearchString);
  const hasCompletedInitialFetch =
    lastCompletedFetchRef.current === bouncedSearchString;

  // Close dropdown when clicking outside the entire command
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if manual entry popover is open
      if (showManualEntry) {
        return;
      }

      if (
        commandRef.current &&
        !commandRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, showManualEntry]);

  // For some reason this autocomplete returns a different data structure than the one from ol server
  //  so we make the conversion here.
  const searchShipsInAutoCompleteFormat: AutoCompleteShipInfo[] =
    useMemo(() => {
      if (!searchShips) return [];
      return searchShips.map((ship) => ({
        name: ship.shipname,
        mmsi: ship.mmsi,
        imo: ship.imo,
        callSign: ship.callsign,
        type: '',
      }));
    }, [searchShips]);

  // Calculate dynamic height based on content
  const calculatedHeight = useMemo(() => {
    const COMMAND_ITEM_HEIGHT = 52; // Increased for two-line display
    const LOADING_SPINNER_HEIGHT = 52;
    const COMMAND_EMPTY_HEIGHT_SMALL = 52; // For "enter three or more characters" message
    const COMMAND_EMPTY_HEIGHT_LARGE = 72; // For "no results" with "Add manually" link
    const PADDING_AND_BORDERS = 10;
    const MAX_HEIGHT = 200;

    let contentHeight = PADDING_AND_BORDERS;

    if (searchString.length < 3) {
      // Show "enter three or more characters" message (smaller box)
      contentHeight += COMMAND_EMPTY_HEIGHT_SMALL;
    } else if (isLoading) {
      // Add loading spinner height
      contentHeight += LOADING_SPINNER_HEIGHT;
    } else if (!searchShipsInAutoCompleteFormat.length) {
      // Add empty state height (with manual entry link - larger box)
      contentHeight += COMMAND_EMPTY_HEIGHT_LARGE;
    } else {
      // Add height for displayed ships
      contentHeight +=
        searchShipsInAutoCompleteFormat.length * COMMAND_ITEM_HEIGHT;
    }

    return Math.min(contentHeight, MAX_HEIGHT);
  }, [searchShipsInAutoCompleteFormat.length, isLoading, searchString.length]);

  const handleManualAdd = React.useCallback(
    (vessel: AutoCompleteShipInfo) => {
      // Check for duplicates based on MMSI
      const isDuplicate = value.some(
        (v) => v.mmsi && vessel.mmsi && v.mmsi === vessel.mmsi,
      );

      if (!isDuplicate) {
        onChange([...value, vessel]);
      }

      setShowManualEntry(false);
      setSearchString('');
    },
    [value, onChange],
  );

  const handleUnselect = React.useCallback(
    (ship: AutoCompleteShipInfo) => {
      // Use MMSI as primary identifier, fall back to name for comparison
      onChange(
        value.filter((s) =>
          ship.mmsi ? s.mmsi !== ship.mmsi : s.name !== ship.name,
        ),
      );
    },
    [value, onChange],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;
      if (input) {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          if (input.value === '') {
            onChange(value.slice(0, -1)); // Removes the last item from the array
          }
        }
        // This is not a default behaviour of the <input /> field
        if (e.key === 'Escape') {
          input.blur();
        }
      }
    },
    [value, onChange],
  );

  return (
    <Command
      ref={commandRef}
      onKeyDown={handleKeyDown}
      className="overflow-visible bg-transparent"
    >
      <div className="group border border-input px-3 py-2 text-sm rounded-md">
        <div className="flex gap-1 flex-wrap">
          {value.map((selectable) => {
            const key =
              selectable.mmsi || selectable.imo || selectable.name || 'unknown';
            return (
              <Badge key={key} variant="secondary">
                {getShipDisplayName(selectable)}
                <button
                  className="ml-1 ring-offset-background rounded-full outline-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleUnselect(selectable);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={() => handleUnselect(selectable)}
                >
                  <XIcon className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                </button>
              </Badge>
            );
          })}
          <CommandPrimitive.Input
            ref={inputRef}
            value={searchString}
            onValueChange={setSearchString}
            onFocus={() => setOpen(true)}
            placeholder="search vessel..."
            className="ml-2 bg-transparent outline-none placeholder:text-muted-foreground flex-1"
          />
        </div>
      </div>
      <div className="relative mt-2">
        {open && searchString.length < 3 && (
          <div
            className="absolute w-full z-10 top-0 rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in"
            style={{ height: `${calculatedHeight}px` }}
          >
            <div className="py-6 text-center text-sm">
              Enter three or more characters to search.
            </div>
          </div>
        )}

        {open && searchString.length >= 3 && (
          <div
            className="absolute w-full z-10 top-0 rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in p-0 overflow-hidden"
            style={{ height: `${calculatedHeight}px` }}
          >
            {isLoading || !hasCompletedInitialFetch ? (
              <div className="h-full flex justify-center items-center">
                <LoadingSpinner size="small" />
              </div>
            ) : searchShipsInAutoCompleteFormat &&
              searchShipsInAutoCompleteFormat.length > 0 ? (
              <ScrollArea className="h-full p-1">
                {searchShipsInAutoCompleteFormat.map((selectable) => {
                  // Check if this ship is already selected
                  const isAlreadySelected = value.some(
                    (selectedShip) => selectedShip.name === selectable.name,
                  );

                  return (
                    <div
                      key={`${selectable.name}-${selectable.mmsi}-${selectable.imo}-${selectable.callSign}`}
                      className={`relative flex cursor-pointer select-none items-center rounded-md px-2 py-1.5 text-sm outline-none ${
                        isAlreadySelected
                          ? 'bg-muted text-muted-foreground cursor-not-allowed'
                          : 'hover:bg-accent hover:text-accent-foreground'
                      }`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={() => {
                        // Only add if not already selected
                        if (!isAlreadySelected) {
                          setSearchString('');
                          onChange([...value, selectable]);
                        }
                      }}
                    >
                      <div className="flex flex-col w-full">
                        <div className="flex items-center space-x-2">
                          <PiBoatThin className="size-4 text-muted-foreground" />
                          <span>{selectable.name}</span>
                        </div>
                        <div className="flex space-x-2 text-xs text-muted-foreground pl-6">
                          <span>MMSI: {selectable.mmsi || 'None'}</span>
                          <span>•</span>
                          <span>IMO: {selectable.imo || 'None'}</span>
                        </div>
                      </div>
                      {isAlreadySelected && (
                        <span className="ml-auto text-xs">
                          Already selected
                        </span>
                      )}
                    </div>
                  );
                })}
              </ScrollArea>
            ) : (
              <div className="py-2 text-center text-sm">
                <div className="text-muted-foreground">
                  {messages.shipCommandNoVesselsFound}
                </div>
                <Popover
                  modal={true}
                  open={showManualEntry}
                  onOpenChange={setShowManualEntry}
                >
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="mt-1 text-primary hover:underline cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowManualEntry(true);
                      }}
                    >
                      {messages.shipCommandAddManuallyLink ||
                        "Can't find vessel? Add manually"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="p-0"
                    align="center"
                    onOpenAutoFocus={(e) => {
                      // Prevent auto-focus from stealing focus from our inputs
                      e.preventDefault();
                    }}
                  >
                    <ManualVesselEntry
                      onAdd={handleManualAdd}
                      onCancel={() => setShowManualEntry(false)}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>
        )}
      </div>
    </Command>
  );
};

type ShipSelectorProps = {
  value: {
    name: string | null;
    mmsi: string | null;
    imo: string | number | null;
    callSign: string | null;
    type: string | null;
  } | null;
  onChange: (
    value: {
      name: string | null;
      mmsi: string | null;
      imo: string | null;
      callSign: string | null;
      type: string | null;
    } | null,
  ) => void;
};

export const ShipSelector = ({ value, onChange }: ShipSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [searchString, setSearchString] = useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { messages } = useLocalization();

  // Using your existing hook (replace with your actual implementation)
  const { displayedShips, uniqueShips, viewportRef, isLoading } =
    useInfiniteSearchShips(25, searchString);

  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
  };

  // Clear search and selection
  const clearSelection = () => {
    onChange(null);
    setSearchString('');
  };

  // Handle ship selection
  const onShipSelect = (ship: ShipSearchResult) => {
    const selectedShip = ship as AutoCompleteShipInfo;

    onChange({
      name: selectedShip?.name || '',
      mmsi: selectedShip?.mmsi?.toString() || null,
      imo: selectedShip?.imo?.toString() || null,
      callSign: selectedShip?.callSign || '',
      type: selectedShip?.type || '',
    });

    setSearchString('');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="w-full flex border rounded-lg justify-between items-center px-3 py-2 cursor-pointer">
          <div className="flex items-center gap-2 flex-grow text-sm">
            {value ? (
              <div className="flex items-center gap-2">
                <PiBoatThin className="size-4" />
                <span>{value.name}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">
                {messages.shipSearchText}
              </span>
            )}
          </div>

          <div className="flex space-x-2">
            {value && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  clearSelection();
                }}
              >
                <XIcon className="size-4" />
              </Button>
            )}
            <Tooltip>
              <TooltipTrigger className="flex items-center">
                <PiInfo className="text-primary" />
              </TooltipTrigger>
              <TooltipContent side="right">
                {messages.shipSearchTooltip}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </PopoverTrigger>

      {/* Dropdown content — portalled so it overflows the dialog instead of being clipped */}
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0"
        onOpenAutoFocus={(e) => {
          // Keep focus on the search input instead of the popover container
          e.preventDefault();
        }}
      >
        {/* Search input */}
        <div className="p-2 border-b">
          <SearchInput
            ref={inputRef}
            autoFocus
            placeholder="Search for vessels..."
            value={searchString}
            onChange={handleSearchChange}
            onClear={() => setSearchString('')}
            className="w-full"
          />
        </div>

        {/* Results area */}
        <div className="pl-4 pt-4">
          <div className="py-1 px-2 text-xs font-medium text-muted-foreground">
            Vessels ({uniqueShips.length})
          </div>

          {!isLoading && searchString.length < 3 && (
            <div className="pt-2 pb-6 text-center text-sm">
              {messages.shipCommandEnterChars}
            </div>
          )}

          {!isLoading &&
            searchString.length >= 3 &&
            displayedShips.length === 0 && (
              <div className="py-6 text-center text-sm">
                {messages.shipCommandNoVesselsFound}
              </div>
            )}

          {isLoading && (
            <div className="flex justify-center py-4">
              <LoadingSpinner size="small" />
            </div>
          )}

          {displayedShips && displayedShips.length > 0 && (
            <ScrollArea className="h-[256px]" ref={viewportRef}>
              {displayedShips.map((ship, index) => (
                <div
                  key={index}
                  className="flex flex-col space-y-1 p-2 hover:bg-accent rounded-lg cursor-pointer mr-4"
                  onClick={() => onShipSelect(ship)}
                >
                  <div className="flex justify-between">
                    <div className="flex items-center space-x-1">
                      <PiBoatThin className="mr-2 size-4" />
                      {ship.name}
                    </div>
                  </div>
                  <div className="flex space-x-2 text-xs text-muted-foreground items-center">
                    <div>MMSI: {ship.mmsi}</div>
                    <div
                      className={cn(
                        'rounded-full h-1 w-1',
                        ship.requestType === 'ais' && 'bg-blue-600',
                        ship.requestType === 'database' && 'bg-red-500',
                      )}
                    />
                    <div>IMO: {ship.imo ? ship.imo : 'None'}</div>
                  </div>
                </div>
              ))}
            </ScrollArea>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
